// Onboarding Walkthrough for First-Time Users
import { useState, useEffect } from 'react';
import { ShoppingBag, Search, CreditCard, Truck, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const ONBOARDING_KEY = 'salesspace-onboarding-complete';

const steps = [
  {
    icon: ShoppingBag,
    title: 'Welcome to SalesSpace',
    description: 'Your one-stop marketplace for amazing products at great prices.',
    color: 'bg-blue-500'
  },
  {
    icon: Search,
    title: 'Browse & Discover',
    description: 'Search products, filter by category, and find exactly what you need.',
    color: 'bg-purple-500'
  },
  {
    icon: CreditCard,
    title: 'Secure Checkout',
    description: 'Pay safely with Stripe, Paystack, or other payment methods.',
    color: 'bg-green-500'
  },
  {
    icon: Truck,
    title: 'Fast Delivery',
    description: 'Track your orders in real-time and get updates on delivery.',
    color: 'bg-orange-500'
  }
];

export default function OnboardingWalkthrough({ onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const completed = localStorage.getItem(ONBOARDING_KEY);
    if (!completed) {
      setIsVisible(true);
    }
  }, []);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = () => {
    localStorage.setItem(ONBOARDING_KEY, 'true');
    setIsVisible(false);
    onComplete?.();
  };

  if (!isVisible) return null;

  const step = steps[currentStep];
  const Icon = step.icon;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/95 p-4">
      <div className="relative w-full max-w-md">
        {/* Skip button */}
        <button
          onClick={handleSkip}
          className="absolute -top-12 right-0 text-slate-400 hover:text-white flex items-center gap-1 text-sm"
          data-testid="onboarding-skip"
        >
          Skip <X className="w-4 h-4" />
        </button>

        {/* Card */}
        <div className="bg-white dark:bg-slate-800 rounded-3xl overflow-hidden shadow-2xl">
          {/* Icon Section */}
          <div className={`${step.color} p-12 flex justify-center`}>
            <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center">
              <Icon className="w-12 h-12 text-white" />
            </div>
          </div>

          {/* Content */}
          <div className="p-8 text-center">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-3">
              {step.title}
            </h2>
            <p className="text-slate-600 dark:text-slate-400 mb-8">
              {step.description}
            </p>

            {/* Progress Dots */}
            <div className="flex justify-center gap-2 mb-8">
              {steps.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentStep(index)}
                  className={`w-2.5 h-2.5 rounded-full transition-all ${
                    index === currentStep
                      ? 'bg-blue-500 w-8'
                      : 'bg-slate-300 dark:bg-slate-600'
                  }`}
                />
              ))}
            </div>

            {/* Navigation */}
            <div className="flex gap-3">
              {currentStep > 0 && (
                <Button
                  variant="outline"
                  onClick={handlePrev}
                  className="flex-1"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Back
                </Button>
              )}
              <Button
                onClick={handleNext}
                className={`flex-1 ${step.color} hover:opacity-90 text-white`}
                data-testid="onboarding-next"
              >
                {currentStep === steps.length - 1 ? (
                  'Get Started'
                ) : (
                  <>
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Hook to reset onboarding (for testing)
export function useOnboarding() {
  const resetOnboarding = () => {
    localStorage.removeItem(ONBOARDING_KEY);
  };

  const isOnboardingComplete = () => {
    return localStorage.getItem(ONBOARDING_KEY) === 'true';
  };

  return { resetOnboarding, isOnboardingComplete };
}
